// Started from shadcn/ui (new-york-v4) "button", restyled to Union (DESIGN.md; design system
// artifact: components/Button). Buttons start the one action a screen is for: one `primary`
// per view, labels that name the outcome ("Book 07:42", never "Submit").

import { cn } from "@union/ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";

const buttonVariants = cva(
  [
    "inline-flex shrink-0 items-center justify-center gap-space-2 whitespace-nowrap rounded-md type-label",
    "border-[1.5px] border-transparent transition-colors duration-150 active:translate-y-px",
    // Disabled reads as unavailable without fading the label below contrast.
    "disabled:pointer-events-none disabled:border-transparent disabled:bg-muted disabled:text-faint-foreground",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:[stroke-width:1.75] [&_svg:not([class*='size-'])]:size-4",
  ],
  {
    variants: {
      variant: {
        /** The club's colour. One per view. */
        primary: "bg-primary text-primary-foreground hover:bg-primary-hover",
        /** Heather: the second kind of thing (events, lessons). Never beside an equal primary. */
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary-hover",
        /** Neutral, bordered: "Add a guest", "Check in". */
        outline: "border-input bg-card text-foreground hover:bg-muted",
        /** Quiet: "See all times", "Manage". */
        ghost: "text-primary-text hover:bg-primary-soft",
        /** Says what it destroys: "Cancel booking". Confirm first (DESIGN.md: destructive actions). */
        destructive: "border-destructive/40 bg-destructive-soft text-destructive hover:border-destructive",
        link: "h-auto border-0 p-0 text-primary-text underline-offset-4 hover:underline disabled:bg-transparent",
      },
      size: {
        /** 44px: member screens and anything touched. */
        default: "h-tap-min px-space-4",
        /** 36px: staff screens on a desktop. */
        sm: "h-9 gap-space-1 px-space-3",
        icon: "size-tap-min",
        "icon-sm": "size-9",
      },
    },
    defaultVariants: { variant: "primary", size: "default" },
  },
);

/** Icon-only buttons (size "icon"/"icon-sm") need an aria-label. */
function Button({
  className,
  variant = "primary",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
